import { Link } from "react-router-dom";

import { useQuery } from "@tanstack/react-query";
import { Download } from "lucide-react";
import * as XLSX from "xlsx";

import { PageContainer } from "~/components/layout/PageContainer";
import { PageHeader } from "~/components/layout/PageHeader";
import { Button } from "~/components/ui/button";
import { Card, CardContent } from "~/components/ui/card";
import { TableSkeleton } from "~/components/ui/skeletons";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "~/components/ui/table";
import { supabase } from "~/integrations/supabase/client";

interface SellerProfile {
  id: string;
  name: string;
  lastname: string;
  email: string;
  phone: string | null;
}

const fetchSellers = async (): Promise<SellerProfile[]> => {
  const { data, error } = await supabase
    .from("profiles")
    .select("id, name, lastname, email, phone")
    .eq("role", "seller")
    .eq("is_active", true)
    .order("name", { ascending: true });

  if (error) throw error;
  return (data as SellerProfile[]) || [];
};

const Vendedores = () => {
  const {
    data: sellers = [],
    isLoading,
    error,
  } = useQuery({
    queryKey: ["sellers", "active"],
    queryFn: fetchSellers,
    staleTime: 5 * 60 * 1000,
  });

  const handleExportExcel = () => {
    const exportData = sellers.map((seller) => ({
      ID: seller.id,
      Nombre: seller.name,
      Apellido: seller.lastname,
      Email: seller.email,
      Teléfono: seller.phone || "-",
    }));

    const worksheet = XLSX.utils.json_to_sheet(exportData);
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, "Vendedores");

    worksheet["!cols"] = [
      { wch: 40 },
      { wch: 20 },
      { wch: 20 },
      { wch: 30 },
      { wch: 15 },
    ];

    XLSX.writeFile(workbook, "vendedores.xlsx");
  };

  return (
    <PageContainer>
      <PageHeader
        title="Vendedores"
        description="Lista de todos los vendedores registrados en el sistema"
        actions={
          <Button
            onClick={handleExportExcel}
            variant="outline"
            size="sm"
            disabled={sellers.length === 0}
          >
            <Download className="mr-2 h-4 w-4" />
            Descargar Excel
          </Button>
        }
      />

      {error ? (
        <div className="flex h-64 items-center justify-center">
          <p className="text-destructive">
            No se pudieron cargar los vendedores. Intente nuevamente.
          </p>
        </div>
      ) : isLoading ? (
        <TableSkeleton rows={8} columns={3} />
      ) : (
        <Card>
          <CardContent className="p-0">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Nombre</TableHead>
                  <TableHead>Email</TableHead>
                  <TableHead>Teléfono</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {sellers.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={3} className="text-center">
                      No hay vendedores registrados
                    </TableCell>
                  </TableRow>
                ) : (
                  sellers.map((seller, index) => (
                    <TableRow
                      key={seller.id}
                      style={{
                        animationDelay: `${Math.min(index * 40, 400)}ms`,
                      }}
                      className="cursor-pointer duration-300 animate-in fade-in-0 slide-in-from-top-1 fill-mode-both hover:bg-muted/50"
                    >
                      <TableCell>
                        <Link
                          to={`/seller/${seller.id}`}
                          className="font-medium hover:underline"
                        >
                          {seller.name} {seller.lastname}
                        </Link>
                      </TableCell>
                      <TableCell className="break-all">
                        {seller.email}
                      </TableCell>
                      <TableCell>{seller.phone || "-"}</TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </CardContent>
        </Card>
      )}
    </PageContainer>
  );
};

export default Vendedores;
